import { Text } from "@chakra-ui/react";
import { formatMoney } from "infra/helpers/number";
import moment from "moment";
import { useMemo } from "react";
import { RelatorioComposicaoReceitasHorizontalItem } from "core/features/relatorios/typings";
import { ColumnType } from "antd/lib/table";
import { formatBlocoQuadra } from "infra/helpers/formatBlocoQuadra";

function useRelatorioComposicaoReceitasHorizontalTableColumns(
  todasAsCategorias?: { id: string; nome: string; breadcrumb: string }[]
) {
  return useMemo(() => {
    const columns: ColumnType<RelatorioComposicaoReceitasHorizontalItem>[] = [
      {
        title: "Unidade",
        render: (original: RelatorioComposicaoReceitasHorizontalItem) => (
          <Text lineHeight={"20px"} fontSize={14}>
            {formatBlocoQuadra(original.unidade, original.blocoQuadra, "")}
          </Text>
        ),
      },
      {
        title: "Nome",
        render: (original: RelatorioComposicaoReceitasHorizontalItem) => (
          <Text lineHeight={"20px"} fontSize={14}>
            {original.pessoa}
          </Text>
        ),
      },
      {
        title: "Competência",
        render: (original: RelatorioComposicaoReceitasHorizontalItem) => (
          <Text lineHeight={"20px"} fontSize={14}>
            {original.dataCompetencia &&
            moment(original.dataCompetencia, "YYYY-MM").isValid()
              ? moment(original.dataCompetencia, "YYYY-MM").format("MM/YYYY")
              : "--"}
          </Text>
        ),
      },
      {
        title: "Vencimento",
        render: (original: RelatorioComposicaoReceitasHorizontalItem) => (
          <Text lineHeight={"20px"} fontSize={14}>
            {original.dataVencimento
              ? moment(original.dataVencimento).format("DD/MM/YYYY")
              : "--"}
          </Text>
        ),
      },
      {
        title: "Liquidação",
        render: (original: RelatorioComposicaoReceitasHorizontalItem) => (
          <Text lineHeight={"20px"} fontSize={14}>
            {original.dataPagamento
              ? moment(original.dataPagamento).format("DD/MM/YYYY")
              : "--"}
          </Text>
        ),
      },
      {
        title: "Crédito",
        render: (original: RelatorioComposicaoReceitasHorizontalItem) => (
          <Text lineHeight={"20px"} fontSize={14}>
            {original.dataMovimentacao
              ? moment(original.dataMovimentacao).format("DD/MM/YYYY")
              : "--"}
          </Text>
        ),
      },
      {
        title: "Nosso Número",
        render: (original: RelatorioComposicaoReceitasHorizontalItem) => (
          <Text lineHeight={"20px"} fontSize={14}>
            {original.nossoNumero ?? "--"}
          </Text>
        ),
      },
    ];

    if (todasAsCategorias?.length) {
      const primeiras3Categorias = todasAsCategorias.slice(0, 3);

      primeiras3Categorias.forEach((categoria) =>
        columns.push({
          title: () => <Text textAlign="right">{categoria.nome}</Text>,
          render: (original: RelatorioComposicaoReceitasHorizontalItem) => (
            <Text lineHeight={"20px"} fontSize={14} textAlign="right">
              {formatMoney(original.composicoes[categoria.id] ?? 0)}
            </Text>
          ),
        })
      );

      if (todasAsCategorias.length > 3) {
        columns.push({
          title: () => <Text textAlign="right">Outras</Text>,
          render: (original: RelatorioComposicaoReceitasHorizontalItem) => {
            const categoriasASomar = Object.entries(
              original.composicoes
            ).filter(
              ([key]) =>
                !primeiras3Categorias.find((categoria) => categoria.id === key)
            );

            const soma = categoriasASomar.reduce(
              (acc, [_, valor]) => (acc += valor),
              0
            );

            return (
              <Text lineHeight={"20px"} fontSize={14} textAlign="right">
                {formatMoney(soma ?? 0)}
              </Text>
            );
          },
        });
      }

      columns.push({
        title: () => <Text textAlign="right">Total</Text>,
        render: (original: RelatorioComposicaoReceitasHorizontalItem) => {
          const categoriasASomar = Object.entries(original.composicoes);

          const soma = categoriasASomar.reduce(
            (acc, [_, valor]) => (acc += valor),
            0
          );

          return (
            <Text lineHeight={"20px"} fontSize={14} textAlign="right">
              {formatMoney(soma ?? 0)}
            </Text>
          );
        },
      });
    }

    return columns;
  }, [todasAsCategorias]);
}

export default useRelatorioComposicaoReceitasHorizontalTableColumns;
